import React, { useEffect, useState } from 'react';

import { Hit, SearchResponse } from '@algolia/client-search';
import { useTrack } from '@blockworks/platform/services/analytics';

import NewsSectionArticle from '@/components/pages/frontpage/components/news/news-section-article';
import { AlgoliaPostHit } from '@/types/algolia/algolia-post-hit';
import { ArticleType } from '@/types/article-type';
import mapAlgoliaHits from '@/utils/functions/algolia/map-algolia-hits';
import queryAlgoliaPosts from '@/utils/functions/algolia/query-algolia-posts';

type SearchPageLayoutProps = {
    initialResults: SearchResponse<any>;
};

const HITS_LIMIT = 12;

const SET_SEARCH_TERM = 'SET_SEARCH_TERM';
const SET_PAGE = 'SET_PAGE';
const searchStateReducer = (state: any, action: any) => {
    switch (action.type) {
        case SET_SEARCH_TERM:
            return { ...state, searchTerm: action.payload, page: 0 };
        case SET_PAGE:
            return { ...state, page: action.payload };
        default:
            return state;
    }
};

// eslint-disable-next-line import/prefer-default-export
const SearchPageLayout = (props: SearchPageLayoutProps) => {
    const { initialResults } = props;
    const [state, dispatch] = React.useReducer(searchStateReducer, {
        searchTerm: '',
        page: 0,
    });
    const [results, setResults] = useState(initialResults);
    const [hits, setHits] = useState<ArticleType[]>(mapAlgoliaHits(initialResults.hits));
    const track = useTrack();
    const currentPageHuman = state.page + 1;
    const totalPagesHuman = Math.floor(results.nbHits / HITS_LIMIT);
    const canLoadMore = results.nbHits > hits.length * state.page;

    useEffect(() => {
        const runQuery = async () => {
            const queryResults = await queryAlgoliaPosts(state.searchTerm, HITS_LIMIT, state.page);
            setResults(queryResults);

            if (state.page > 0) {
                setHits([...hits, ...mapAlgoliaHits(queryResults.hits as Array<Hit<AlgoliaPostHit>>)]);
            } else {
                setHits(mapAlgoliaHits(queryResults.hits as Array<Hit<AlgoliaPostHit>>));
            }

            track('Hits Viewed');
        };

        runQuery();
        track('Search Submitted', { searchValue: state.searchTerm });
    }, [state.searchTerm, state.page]);

    const handleSearchInput = (ev: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({ type: SET_SEARCH_TERM, payload: ev.target.value });
    };

    const handleLoadMore = () => {
        dispatch({ type: SET_PAGE, payload: state.page + 1 });
    };

    return (
        <div className="w-full flex-col">
            <div className="flex p-6 md:p-10 lg:p-12 bg-gray-50">
                <div className="mx-auto w-full lg:w-[80%]">
                    <input
                        id="blockworks-search"
                        type="text"
                        onChange={handleSearchInput}
                        placeholder="Search..."
                        className="w-full min-w-0 appearance-none rounded-none border border-gray-200 hover:border-gray-300 bg-white py-2 px-4 text-base text-gray-900 placeholder-gray-300 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        autoFocus
                    />
                </div>
            </div>
            <div className="p-8">
                <div className="w-full flex place-content-center pb-4 -mt-4">
                    <p className="text-xxs font-light flex flex-row gap-1">
                        <span className="text-gray-400">
                            Page {currentPageHuman}/{totalPagesHuman} -{' '}
                        </span>
                        <span className="text-gray-400">{results.nbHits} total hits</span>
                        <span className="text-gray-300">(~{results.processingTimeMS}ms)</span>
                    </p>
                </div>

                <div className="grid gap-8 grid-cols-1 xs:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
                    {hits.length === 0 && (
                        <div>
                            <p className="text-lg">Your search did not return any results.</p>
                        </div>
                    )}
                    {hits.map(article => (
                        <NewsSectionArticle
                            article={article}
                            key={`NewsSectionArticle-${article.slug}`}
                            onClick={() =>
                                track('Search Hit Clicked', {
                                    searchTerm: state.searchTerm,
                                    slug: article.slug,
                                    title: article.title,
                                    author: article.authors.map(a => a.title).join(','),
                                })
                            }
                        />
                    ))}
                </div>
            </div>
            {canLoadMore && (
                <div className="p-12 w-full">
                    <div className="mx-auto">
                        <button
                            type="button"
                            onClick={handleLoadMore}
                            className="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        >
                            Load More
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchPageLayout;
